import { INDUSTRIES } from '@/config/constants';
import { Permission, useAuth } from '@/contexts/AuthContext';
import { agentService } from '@common/api/agentService';
import { supabase } from '@common/api/authService';
import {
    CaretDown,
    Check,
    CircleNotch,
    Upload
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
    useEffect, useRef, useState
} from 'react';

// WorkspaceAvatar component - shows logo or fallback to initial
interface WorkspaceAvatarProps {
    logoUrl?: string;
    name: string;
    className?: string;
    fontSize?: string;
}

const WorkspaceAvatar = ({
    logoUrl,
    name,
    className,
    fontSize = 'text-sm'
}: WorkspaceAvatarProps) => {
    const [imageError, setImageError] = useState(false);

    if (logoUrl && !imageError) {
        return (
            <img
                src={logoUrl}
                alt={name}
                className={clsx('w-full h-full object-contain p-0.5', className)}
                onError={() => setImageError(true)}
            />
        );
    }

    return (
        <div
            className={clsx(
                'flex items-center justify-center w-full h-full font-bold text-gray-800 bg-gray-200',
                fontSize,
                className
            )}
        >
            {name?.charAt(0).toUpperCase() || 'W'}
        </div>
    );
};

const WorkspaceSettings = () => {
    const {
        currentWorkspace, refreshWorkspaces, permissions
    } = useAuth();
    const canEdit = permissions.checkPermissions([Permission.WORKSPACE_UPDATE]);
    const [workspaceName, setWorkspaceName] = useState(currentWorkspace?.name || '');
    const [logoUrl, setLogoUrl] = useState(currentWorkspace?.logo_url || '');
    const [industry, setIndustry] = useState('ecommerce');
    const [otherIndustry, setOtherIndustry] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);

    const logoInputRef = useRef<HTMLInputElement>(null);

    // Load initial data
    useEffect(() => {
        if (currentWorkspace) {
            setWorkspaceName(currentWorkspace.name);
            setLogoUrl(currentWorkspace.logo_url || '');
            const currentIndustry = currentWorkspace.settings?.industry || 'ecommerce';
            // 检查是否在预定义列表中，不在则视为 other
            const industryExists = INDUSTRIES.find(i => i.id === currentIndustry);
            if (industryExists) {
                setIndustry(currentIndustry);
                setOtherIndustry('');
            } else {
                setIndustry('other');
                setOtherIndustry(currentIndustry);
            }
        }
    }, [currentWorkspace]);

    // Handle logo upload
    const handleLogoUpload = async (file: File) => {
        if (!file) return;

        setIsUploading(true);
        try {
            // Use agentService to upload file
            const response = await agentService.uploadFile(file);

            if (response.success && response.file_url) {
                setLogoUrl(response.file_url);
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
        } finally {
            setIsUploading(false);
        }
    };

    // Handle save
    const handleSave = async () => {
        if (!currentWorkspace?.id) return;

        setIsSaving(true);
        try {
            const updateData = {
                name: workspaceName,
                logo_url: logoUrl,
                settings: {
                    ...(currentWorkspace.settings || {}),
                    // 如果选择 other，保存用户输入的值，否则保存选中的 id
                    industry: industry === 'other' ? otherIndustry : industry,
                },
                updated_at: new Date().toISOString(),
            };

            // @ts-ignore
            const { error } = await supabase
                .from('workspaces')
                // @ts-ignore
                .update(updateData)
                .eq('id', currentWorkspace.id);

            if (error) throw error;

            // Refresh workspaces to sync data in useWorkspaces hook
            await refreshWorkspaces({ silent: true });
        } catch (error) {
            console.error('Error saving workspace:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const selectedIndustryData = INDUSTRIES.find((i) => i.id === industry);
    // 获取实际要保存的 industry 值
    const finalIndustry = industry === 'other' ? otherIndustry : industry;
    const hasChanges = workspaceName !== currentWorkspace?.name
        || logoUrl !== currentWorkspace?.logo_url
        || finalIndustry !== currentWorkspace?.settings?.industry;

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="space-y-6">
                    {/* Logo Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                            Workspace Logo
                        </label>
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border border-gray-200 overflow-hidden">
                                <WorkspaceAvatar
                                    logoUrl={logoUrl}
                                    name={workspaceName}
                                />
                            </div>
                            <button
                                onClick={() => logoInputRef.current?.click()}
                                disabled={isUploading || !canEdit}
                                className={clsx(
                                    'flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-bold transition-colors disabled:opacity-50',
                                    canEdit ? 'border-gray-200 text-gray-900 hover:bg-gray-50' : 'border-transparent text-gray-400 cursor-not-allowed'
                                )}
                            >
                                {isUploading ? (
                                    <>
                                        <CircleNotch size={16} weight="bold" className="animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload size={16} weight="bold" />
                                        Upload Logo
                                    </>
                                )}
                            </button>
                            <input
                                type="file"
                                ref={logoInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleLogoUpload(file);
                                }}
                            />
                        </div>
                    </div>

                    {/* Workspace Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                            Workspace Name
                        </label>
                        <input
                            type="text"
                            value={workspaceName}
                            onChange={(e) => setWorkspaceName(e.target.value)}
                            disabled={!canEdit}
                            className={clsx(
                                'w-full max-w-md px-4 py-2 border rounded-lg outline-none transition-all',
                                canEdit
                                    ? 'border-gray-200 focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F] bg-white'
                                    : 'border-transparent bg-gray-50 text-gray-600 cursor-not-allowed'
                            )}
                        />
                    </div>

                    {/* Industry Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                            What is your industry?
                        </label>
                        <DropdownMenu.Root
                            open={isIndustryDropdownOpen}
                            onOpenChange={(open) => canEdit && setIsIndustryDropdownOpen(open)}
                        >
                            <DropdownMenu.Trigger asChild>
                                <div
                                    className={clsx(
                                        'w-full max-w-md px-4 py-2.5 border rounded-lg flex items-center justify-between transition-all',
                                        canEdit
                                            ? 'bg-white cursor-pointer border-gray-200 hover:border-gray-300'
                                            : 'bg-gray-50 border-transparent cursor-not-allowed',
                                        isIndustryDropdownOpen && canEdit && 'border-[#1A162F] ring-2 ring-[#1A162F]/20'
                                    )}
                                >
                                    {selectedIndustryData ? (
                                        <div className="flex items-center gap-2">
                                            <selectedIndustryData.icon size={20} weight="bold" className={canEdit ? 'text-gray-900' : 'text-gray-400'} />
                                            <span className={clsx('font-medium', canEdit ? 'text-gray-900' : 'text-gray-600')}>{selectedIndustryData.label}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-500">Select your industry...</span>
                                    )}
                                    <CaretDown
                                        size={16}
                                        weight="bold"
                                        className={clsx(
                                            'transition-transform duration-200',
                                            canEdit ? 'text-gray-500' : 'text-gray-400',
                                            isIndustryDropdownOpen && 'rotate-180'
                                        )}
                                    />
                                </div>
                            </DropdownMenu.Trigger>

                            <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                    className="z-[10002] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top"
                                    sideOffset={6}
                                    align="start"
                                    style={{ width: 'var(--radix-dropdown-menu-trigger-width)', maxWidth: 'calc(100vw - 16px)' }}
                                >
                                    <div className="max-h-[280px] overflow-y-auto p-1.5 space-y-0.5">
                                        {INDUSTRIES.map((ind) => {
                                            const isSelected = industry === ind.id;
                                            const Icon = ind.icon;
                                            return (
                                                <DropdownMenu.Item
                                                    key={ind.id}
                                                    onSelect={() => {
                                                        setIndustry(ind.id);
                                                        setIsIndustryDropdownOpen(false);
                                                    }}
                                                    className={clsx(
                                                        'flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-colors group outline-none',
                                                        isSelected ? 'bg-[#1A162F]/5' : 'hover:bg-gray-50'
                                                    )}
                                                >
                                                    <div
                                                        className={clsx(
                                                            'p-2 rounded-lg shrink-0 transition-colors',
                                                            isSelected
                                                                ? 'bg-[#1A162F] text-white'
                                                                : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-900'
                                                        )}
                                                    >
                                                        <Icon size={20} weight="bold" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <span
                                                                className={clsx(
                                                                    'font-semibold text-sm truncate pr-2',
                                                                    isSelected ? 'text-[#1A162F]' : 'text-gray-900'
                                                                )}
                                                            >
                                                                {ind.label}
                                                            </span>
                                                            {isSelected ? (
                                                                <Check size={16} weight="bold" className="text-[#1A162F] shrink-0" />
                                                            ) : null}
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                                            {ind.desc}
                                                        </p>
                                                    </div>
                                                </DropdownMenu.Item>
                                            );
                                        })}
                                    </div>
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>

                        {/* Other Industry Input */}
                        {industry === 'other' && (
                            <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-900 mb-1">
                                    Please specify your industry
                                </label>
                                <input
                                    type="text"
                                    value={otherIndustry}
                                    onChange={(e) => setOtherIndustry(e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="e.g. Manufacturing, Real Estate, etc."
                                    className={clsx(
                                        'w-full max-w-md px-4 py-2 border rounded-lg outline-none transition-all',
                                        canEdit
                                            ? 'border-gray-200 focus:ring-2 focus:ring-[#1A162F]/20 focus:border-[#1A162F] bg-white'
                                            : 'border-transparent bg-gray-50 text-gray-600 cursor-not-allowed'
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Save Button */}
                {hasChanges && canEdit ? (
                    <div className="mt-6 pt-6 border-t border-gray-100 animate-in fade-in slide-in-from-top-2 duration-300">
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-4 py-2 bg-[#1A162F] text-white rounded-lg font-medium text-sm hover:bg-[#1A162F]/90 transition-colors disabled:opacity-70 shadow-sm"
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default WorkspaceSettings;
